import { enqueueSnackbar } from 'notistack';
import { useMemo, useState } from 'react';

import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { IInboundEmailLog } from 'src/services/inbound-email/inbound-email.types';
import { useGetOrganisationJobsQuery } from 'src/services/jobs/jobs.service';
import { useManuallyAssignInboundEmailLogMutation } from 'src/services/inbound-email/inbound-email.service';

type Props = {
  open: boolean;
  onClose: () => void;
  log: IInboundEmailLog | null;
};

export default function ManuallyAssignModal({ open, onClose, log }: Props) {
  const authContext = useAuthContext();
  const orgId = authContext.organization.id;

  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);

  const { currentData: jobsResponse } = useGetOrganisationJobsQuery(
    { page: 1, per_page: 100 } as any,
    { skip: !open }
  );

  const [manuallyAssign, { isLoading }] = useManuallyAssignInboundEmailLogMutation();

  const jobOptions = useMemo(
    () => (jobsResponse?.results ?? []).map((job: any) => ({
      id: job.id,
      label: `${job.title}${job.job_board_code ? ` (${job.job_board_code})` : ''}`,
    })),
    [jobsResponse]
  );

  const handleAssign = async () => {
    if (!log || !selectedJobId) return;
    try {
      await manuallyAssign({ organizationId: orgId, logId: log.id, jobId: selectedJobId }).unwrap();
      enqueueSnackbar('Application created from inbound email', { variant: 'success' });
      setSelectedJobId(null);
      onClose();
    } catch (e: any) {
      const msg = e?.data?.message ?? 'Failed to assign to job';
      enqueueSnackbar(msg, { variant: 'error' });
    }
  };

  const handleClose = () => {
    setSelectedJobId(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Manually assign to a job</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {log && (
            <Typography variant="body2" color="text.secondary">
              Will create an application for{' '}
              <strong>
                {log.parsed_data?.first_name} {log.parsed_data?.last_name}
              </strong>
              {log.parsed_data?.email ? ` (${log.parsed_data.email})` : ''} on the selected job.
            </Typography>
          )}

          <Autocomplete
            options={jobOptions}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(_event, value) => setSelectedJobId(value?.id ?? null)}
            renderInput={(params) => (
              <TextField {...params} label="Job" placeholder="Search jobs..." />
            )}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>Cancel</Button>
        <Button variant="contained" onClick={handleAssign} disabled={!selectedJobId || isLoading}>
          Assign
        </Button>
      </DialogActions>
    </Dialog>
  );
}
